'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterState {
  maxPrice: number;
  stops: string[];
  airlines: string[];
  departureTime: string[];
  maxDuration: number;
  minRating: number;
  amenities: string[];
  dealTypes: string[];
}

interface Props {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  activeTab: 'flights' | 'hotels' | 'packages';
}

const airlines = ['British Airways', 'American Airlines', 'Delta', 'United', 'Lufthansa', 'Emirates', 'Air France'];
const amenities = ['Free WiFi', 'Free Parking', 'Pool', 'Breakfast Included', 'Gym', 'Air Conditioning', 'Pet Friendly'];
const dealTypes = ['Best Value', 'Cheapest', 'Fastest', 'Flash Deal'];

function FilterSection({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="filter-section">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-left mb-3"
      >
        <span className="text-sm font-700 text-foreground">{title}</span>
        <Icon name={open ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} className="text-muted-foreground" />
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

function CheckboxItem({
  label,
  checked,
  onChange,
  count,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  count?: number;
}) {
  return (
    <label className="flex items-center justify-between py-1.5 cursor-pointer group">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="checkbox-custom"
        />
        <span className="text-sm text-foreground group-hover:text-primary transition-colors">{label}</span>
      </div>
      {count !== undefined && (
        <span className="text-xs text-muted-foreground">{count}</span>
      )}
    </label>
  );
}

export default function FilterSidebar({ filters, setFilters, activeTab }: Props) {
  const toggleArray = (key: keyof FilterState, value: string) => {
    setFilters((prev) => {
      const arr = prev[key] as string[];
      return {
        ...prev,
        [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-700 text-foreground">Filters</h3>
        <button
          type="button"
          onClick={() =>
            setFilters({
              maxPrice: 1200,
              stops: [],
              airlines: [],
              departureTime: [],
              maxDuration: 24,
              minRating: 0,
              amenities: [],
              dealTypes: [],
            })
          }
          className="text-xs text-primary font-semibold hover:underline"
        >
          Clear all
        </button>
      </div>

      {/* Price Range */}
      <FilterSection title="Price Range">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Up to</span>
            <span className="font-700 text-primary tabular-nums">${filters.maxPrice}</span>
          </div>
          <input
            type="range"
            min={50}
            max={2000}
            step={25}
            value={filters.maxPrice}
            onChange={(e) => setFilters((p) => ({ ...p, maxPrice: Number(e.target.value) }))}
            className="range-slider"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>$50</span>
            <span>$2,000+</span>
          </div>
        </div>
      </FilterSection>

      {/* Stops (Flights only) */}
      {activeTab !== 'hotels' && (
        <FilterSection title="Stops">
          {[
            { label: 'Nonstop only', value: 'nonstop', count: 34 },
            { label: '1 stop', value: '1-stop', count: 128 },
            { label: '2+ stops', value: '2-plus', count: 86 },
          ].map((item) => (
            <CheckboxItem
              key={`filter-stop-${item.value}`}
              label={item.label}
              checked={filters.stops.includes(item.value)}
              onChange={() => toggleArray('stops', item.value)}
              count={item.count}
            />
          ))}
        </FilterSection>
      )}

      {/* Airlines (Flights only) */}
      {activeTab === 'flights' && (
        <FilterSection title="Airlines">
          {airlines.map((airline) => (
            <CheckboxItem
              key={`filter-airline-${airline}`}
              label={airline}
              checked={filters.airlines.includes(airline)}
              onChange={() => toggleArray('airlines', airline)}
            />
          ))}
        </FilterSection>
      )}

      {/* Departure Time (Flights) */}
      {activeTab === 'flights' && (
        <FilterSection title="Departure Time">
          {[
            { label: 'Early morning (12am–6am)', value: 'early' },
            { label: 'Morning (6am–12pm)', value: 'morning' },
            { label: 'Afternoon (12pm–6pm)', value: 'afternoon' },
            { label: 'Evening (6pm–12am)', value: 'evening' },
          ].map((item) => (
            <CheckboxItem
              key={`filter-dep-${item.value}`}
              label={item.label}
              checked={filters.departureTime.includes(item.value)}
              onChange={() => toggleArray('departureTime', item.value)}
            />
          ))}
        </FilterSection>
      )}

      {/* Duration (Flights) */}
      {activeTab === 'flights' && (
        <FilterSection title="Max Duration">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Up to</span>
              <span className="font-700 text-primary tabular-nums">{filters.maxDuration}h</span>
            </div>
            <input
              type="range"
              min={2}
              max={30}
              step={1}
              value={filters.maxDuration}
              onChange={(e) => setFilters((p) => ({ ...p, maxDuration: Number(e.target.value) }))}
              className="range-slider"
            />
          </div>
        </FilterSection>
      )}

      {/* Hotel Rating */}
      {activeTab === 'hotels' && (
        <FilterSection title="Hotel Rating">
          <div className="flex items-center gap-2 flex-wrap">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={`filter-star-${star}`}
                type="button"
                onClick={() => setFilters((p) => ({ ...p, minRating: p.minRating === star ? 0 : star }))}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                  filters.minRating === star
                    ? 'bg-primary text-white border-primary' :'bg-card text-muted-foreground border-border hover:border-primary'
                }`}
              >
                {'★'.repeat(star)}
              </button>
            ))}
          </div>
        </FilterSection>
      )}

      {/* Amenities (Hotels) */}
      {activeTab === 'hotels' && (
        <FilterSection title="Amenities">
          {amenities.map((item) => (
            <CheckboxItem
              key={`filter-amenity-${item}`}
              label={item}
              checked={filters.amenities.includes(item)}
              onChange={() => toggleArray('amenities', item)}
            />
          ))}
        </FilterSection>
      )}

      {/* Deal Type */}
      <FilterSection title="Deal Type" defaultOpen={false}>
        {dealTypes.map((type) => (
          <CheckboxItem
            key={`filter-deal-${type}`}
            label={type}
            checked={filters.dealTypes.includes(type)}
            onChange={() => toggleArray('dealTypes', type)}
          />
        ))}
      </FilterSection>
    </div>
  );
}